import { Box, Typography, Tooltip, Chip, Grid, Divider, Checkbox } from "@mui/joy";
import { PackageCheck, Info, Clock, Save, Check, CheckCheck, ImageUp } from "lucide-react";
import moment from "moment";
import { useEffect } from "react";

import { DocumentPreviewWidget, Comments, DocumentUploadWidget, AlbumWidget } from "../../../components";
import { Button } from "../../../shared";
import { useFinalSettlement } from "../hooks";

import { PropertyPhotos } from "./PropertyPhotos";

const taskDetails = `The purpose of this task is to complete the final settlement process for the roofing project. 
This involves reviewing all project deliverables, confirming that all contractual obligations have been met, 
verifying completion of punch list items, and ensuring all payments are processed in accordance with the contract.`;

function TaskDetails() {
    return (
        <Box sx={{ minWidth: 300, maxWidth: "100%", width: 400, p: 1 }}>
            <Typography>{taskDetails}</Typography>
        </Box>
    );
}

interface ITaskComponent {
    activeTask: any;
    onTaskClose: any;
    onProjectClose: any;
    actionProgress: any;
}

export default function TaskComponent(props: ITaskComponent) {
    const { activeTask, onProjectClose, onTaskClose } = props;
    const { CreatedAt, Status } = activeTask;

    const { documents } = useFinalSettlement();

    useEffect(() => {
        const scrollableDiv = document.getElementById("project-details-parent");
        if (scrollableDiv) {
            //scrollableDiv.scrollTop = 0; // or use scrollTo
            scrollableDiv.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, []);

    return (
        <Box>
            <Box mt={2} ml={1} mr={1}>
                <Typography startDecorator={<PackageCheck size={16} />} level="body-sm" gutterBottom>
                    Task
                </Typography>
                <Typography
                    level="h4"
                    sx={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 0.5 }}
                    component={"div"}
                >
                    Final Settlement
                    <Tooltip
                        arrow
                        title={<TaskDetails />}
                        variant="outlined"
                        sx={{ border: "1px solid", borderColor: "divider" }}
                    >
                        <Info size={12} />
                    </Tooltip>
                </Typography>

                <Box mt={2} display={"flex"} gap={1}>
                    <Chip color="warning" sx={{ fontSize: 12 }}>
                        {Status}
                    </Chip>
                    <Chip color="neutral" variant="outlined" sx={{ fontSize: 12 }} startDecorator={<Clock size={14} />}>
                        Created On: {moment(CreatedAt).format("MM/DD/YYYY")}
                    </Chip>
                </Box>

                <Box mt={2}>
                    <Box>
                        <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                            Final Invoice & Documents
                        </Typography>
                    </Box>

                    <Box mt={1}>
                        <Grid container spacing={2}>
                            {finalSettlementFiles.map((e: any) => (
                                <Grid>
                                    <DocumentPreviewWidget
                                        key={e.id}
                                        fileType={e.fileType}
                                        title={e.title}
                                        description={e.description}
                                        thumbnailUrl={documents?.[e.id]?.thumbnailUrl}
                                        status={documents?.[e.id]?.Status}
                                        assetId={documents?.[e.id]?.DocumentAssetId}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Box>
                <Box mt={2}>
                    <Box>
                        <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                            Work Completion Confirmation
                        </Typography>
                    </Box>

                    <Grid container spacing={2} mt={1}>
                        <PropertyPhotos taskDetails={activeTask} />
                    </Grid>
                </Box>
                <Box mt={2}>
                    <Box>
                        <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                            Client Sign-Off
                        </Typography>
                    </Box>
                    <Box mt={1} gap={1} display={"flex"} alignItems={"center"}>
                        <DocumentPreviewWidget
                            title="Template"
                            description="Standard template for client approval documentation"
                            thumbnailUrl={documents?.["CLIENT_SIGNOFF"]?.thumbnailUrl}
                            status={documents?.["CLIENT_SIGNOFF"]?.Status}
                            assetId={documents?.["CLIENT_SIGNOFF"]?.DocumentAssetId}
                        />
                        <DocumentUploadWidget
                            title="Upload Sign-Off"
                            description=".doc, .pdf, .jpg, .jpeg are supported"
                        />
                    </Box>
                </Box>
                <Box mt={2}>
                    <Box>
                        <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                            Lien Waivers & Payment Protection
                        </Typography>
                    </Box>

                    <Box mt={1} gap={1} display={"flex"} alignItems={"center"}>
                        <DocumentPreviewWidget
                            title="Lien Waiver & Payment Protection"
                            description="Template for securing payment and waiving lien rights in construction projects"
                            thumbnailUrl={documents?.["LIEN_WAIVER"]?.thumbnailUrl}
                            status={documents?.["LIEN_WAIVER"]?.Status}
                            assetId={documents?.["LIEN_WAIVER"]?.DocumentAssetId}
                        />
                        <DocumentUploadWidget
                            title="Upload Documents"
                            description=".doc, .pdf, .jpg, .jpeg are supported"
                        />
                    </Box>
                </Box>
                <Box mt={2}>
                    <Box>
                        <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                            Cleanup & Site Clearance
                        </Typography>
                    </Box>

                    <Box mt={2}>
                        {cleanupQuestions.map((e: any) => {
                            return (
                                <Box display={"flex"} gap={1} key={e.id} alignItems={"center"}>
                                    <Checkbox checked={true} size="sm" />
                                    <Typography
                                        level="body-sm"
                                        sx={{ color: "text.primary", fontWeight: 500 }}
                                        gutterBottom
                                    >
                                        {e.question}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
                <Divider sx={{ mt: 3 }} />
                <Box mt={2}>
                    <Box flex={1} mb={1}>
                        <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                            Comments
                        </Typography>
                        <Typography level={"body-md"} sx={{ color: "text.primary" }}>
                            Additional comments or any other details you like to add about this project
                        </Typography>
                    </Box>

                    <Comments />
                </Box>
                <Divider sx={{ mt: 2 }} inset="context" />
                <Box mt={2} display={"flex"} gap={1}>
                    <Button startDecorator={<Check size={18} />} onClick={onTaskClose}>
                        Close task
                    </Button>
                    <Button startDecorator={<CheckCheck size={18} />} onClick={onProjectClose}>
                        Close pending tasks & project
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

const finalSettlementFiles = [
    {
        id: "HOUSE_OWNER_INVOICE",
        title: "Hours Owner Invoice",
        fileType: "PDF",

        description: "Detailed breakdown of labor hours billed to the property owner.",
    },
    {
        id: "INSURANCE_INVOICE",
        title: "Insurance Invoice",
        fileType: "PDF",
        description: "Invoice formatted for insurance claim or reimbursement purposes.",
    },
    {
        id: "ROOFER_INVOICE",
        title: "Roofer Invoice",
        fileType: "CSV",
        description: "Raw data of hours, labor cost, and tasks completed by the roofer.",
    },
];

const cleanupQuestions = [
    {
        id: "debrisRemoved",
        question: "Have all roofing materials and debris been removed from the site?",
        type: "boolean",
    },
    {
        id: "magnetSweep",
        question: "Was a magnet sweep performed to pick up loose nails or metal?",
        type: "boolean",
    },
    {
        id: "dumpsterRemoved",
        question: "Has the dumpster or disposal container been removed?",
        type: "boolean",
    },
    {
        id: "guttersCleared",
        question: "Were gutters cleaned of any leftover roofing materials?",
        type: "boolean",
    },
    {
        id: "yardInspected",
        question: "Was the yard and driveway inspected for leftover tools or debris?",
        type: "boolean",
    },
    {
        id: "clientWalkthrough",
        question: "Was a final walkthrough performed with the property owner?",
        type: "boolean",
    },
    {
        id: "cleanupPhotos",
        question: "Have final cleanup photos been taken and uploaded?",
        type: "file-upload",
        fileType: "Image (JPG/PNG)",
    },
];

const categories = [
    "Front of house showing address",
    "Overview of each slope",
    "Close up of every penetration (pipe jack, roof vent, bath vent, furnace vent, etc.)",
    "All metal flashings (drip edge, step flashing, kickouts, headwall, etc)",
    "Exposed Ridge cap nails, showing they have been sealed.",
];
